import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { shopTalk } from '../services/recordShop';
import { lineClip } from '../services/ensemble';
import { audioEngine } from '../services/audioEngine';
import { edgeInset, IS_GUEST } from '../config';

// ShopDock — the counter at Wax & Vinyl.
//
// Khari, 2026-08-04: "he will have tool calling to get info about (and
// recommend) any track in our database, determined by what the user is looking
// for.. As he makes suggestions, they will show on the wall where the records
// are at."
//
// THE WALL IS THE OUTPUT, NOT THIS PANEL. Everything he pulls goes on the
// sleeves behind him — this is only the talking. So the dock stays deliberately
// small and does not list tracks: two surfaces showing the same six records
// would make the wall decorative, and the wall is the thing you can play, like
// and (later) buy from. When he changes it, this says one quiet line and gets
// out of the way.
//
// HE SPEAKS. The reply carries his voice on the transient path (flash model,
// memory only — a clerk's sentences are one-offs and never worth caching to a
// bucket), as a URL when it is ready or a poll key while it renders. Text lands
// first either way; a line that never arrives just stays written.
//
// The turn contract (message + history in, reply + shelf + clip out) is the
// durable part; when he moves to the ConvAI agent the mic opens and the wall
// gets painted by a CLIENT tool instead of by this reply, and nothing else here
// has to change.
const ACCENT = '#9dff7d';

const wrap = {
  position: 'fixed', right: edgeInset(16, 'right'), bottom: 'max(16px, env(safe-area-inset-bottom))',
  zIndex: 70, width: 'min(340px, calc(100vw - 32px))', maxHeight: 'min(460px, 70vh)',
  display: 'flex', flexDirection: 'column', pointerEvents: 'auto', fontFamily: 'system-ui, sans-serif',
  background: 'rgba(8,12,9,0.94)', border: '1px solid rgba(157,255,125,0.34)', borderRadius: 16,
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
};
const head = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '11px 13px', borderBottom: '1px solid rgba(157,255,125,0.18)',
};
const bubble = (mine) => ({
  alignSelf: mine ? 'flex-end' : 'flex-start',
  maxWidth: '86%', padding: '8px 11px', borderRadius: 12,
  background: mine ? 'rgba(157,255,125,0.14)' : 'rgba(255,255,255,0.07)',
  color: mine ? '#e8ffe0' : '#eef2ea', font: '14px/1.4 system-ui',
});
const chip = {
  cursor: 'pointer', background: 'rgba(157,255,125,0.1)', color: ACCENT,
  border: '1px solid rgba(157,255,125,0.32)', borderRadius: 999,
  padding: '6px 11px', font: '12.5px system-ui', whiteSpace: 'nowrap',
};
const inputStyle = {
  flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(157,255,125,0.28)',
  background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 15, fontFamily: 'inherit',
  outline: 'none', boxSizing: 'border-box', minWidth: 0,
};

// What people actually walk in and say. Three, because a wall of suggestions is
// a form, and a form is the thing this whole feature exists to not be.
const OPENERS = ['Something uptempo', 'New R&B', 'Something moody for tonight'];

export default function ShopDock() {
  const shop = useWorldStore((s) => s.shop);
  const closeShop = useWorldStore((s) => s.closeShop);
  const setShelf = useWorldStore((s) => s.setShelfTracks);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const endRef = useRef(null);

  // A new counter is a new conversation. Nothing carries over from the last
  // clerk you talked to, which matters the moment there are two of them.
  useEffect(() => {
    if (!shop) return;
    setMessages([]); setInput(''); setNote('');
  }, [shop?.actorKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  // WALKING OUT ENDS THE CONVERSATION. The counter belongs to a room, and a dock
  // that survived the doorway would leave you talking to a clerk you can no
  // longer see — and would leave his records hanging on a wall in another shop.
  const interior = useWorldStore((s) => s.interior);
  useEffect(() => {
    if (shop?.zone && interior !== shop.zone) closeShop();
  }, [interior, shop?.zone, closeShop]);

  // ── HIS VOICE ─────────────────────────────────────────────────────────────
  //
  // Khari, 2026-08-04: "the music genius should speak, not just text."
  //
  // One audio element for the counter, so a new answer cuts off the last one
  // rather than talking over it — the same single-source rule the rest of the
  // world's speech follows. The music DIPS to the standard duck while he talks
  // (Khari, 2026-08-03: "not muted.. same level of standard ducking") and comes
  // back up when he stops, including when you walk out mid-sentence.
  const voice = useRef(null);
  const ducked = useRef(false);

  const undip = () => { if (ducked.current) { ducked.current = false; audioEngine.duck(false); } };
  const play = (url) => {
    try {
      voice.current?.pause();
      const a = new Audio(url);
      voice.current = a;
      a.onended = undip;
      a.onerror = undip;
      if (!ducked.current) { ducked.current = true; audioEngine.duck(true); }
      a.play().catch(() => undip());   // autoplay refused — the text still reads
    } catch { undip(); }
  };

  // A clip that was still rendering when the reply landed. Same poll route every
  // other spoken line uses; it gives up rather than nagging, and a line that
  // never arrives simply stays text.
  const speak = (d) => {
    if (d.voiced === false) {
      // Loud in the console, quiet in the room: the customer does not need to
      // know the casting is unfinished, and whoever is building it does.
      console.warn(`[shop] ${shop?.name || 'clerk'} is silent — ${d.voiceReason || 'no voice'}`);
      setNote('no voice cast for this part yet');
      return;
    }
    if (d.audioUrl) { play(d.audioUrl); return; }
    if (!d.audioKey) return;
    // TIGHT POLL, because this gap is the whole perceived latency of his voice:
    // the flash synth takes ~1-2s, so asking at 600ms + 700ms intervals could
    // sit on a finished clip for most of a second. First ask at 350ms, then
    // every 400ms — same ~9s budget, a third of the wait.
    let tries = 0;
    const tick = async () => {
      tries += 1;
      const url = await lineClip(d.audioKey);
      if (url) { play(url); return; }
      if (tries >= 22) { console.warn('[shop] clip never landed — the line stays text'); return; }
      setTimeout(tick, 400);
    };
    setTimeout(tick, 350);
  };

  useEffect(() => () => { voice.current?.pause(); undip(); }, []);   // leaving stops him

  if (!shop) return null;

  const send = async (text) => {
    const t = String(text || '').trim();
    if (!t || busy) return;
    const history = messages.map((m) => ({ role: m.role, content: m.text }));
    setMessages((cur) => [...cur, { role: 'user', text: t }]);
    setInput('');
    setBusy(true);
    setNote('');
    try {
      const d = await shopTalk({ actorKey: shop.actorKey, zone: shop.zone, message: t, history });
      setMessages((cur) => [...cur, { role: 'assistant', text: d.reply || '…' }]);
      speak(d);
      // Only when he actually changed the wall — see the service note. Re-hanging
      // an identical shelf would restart every animated sleeve on it.
      if (d.shelf) {
        setShelf(d.shelf);
        setNote(d.shelf.length ? `▸ ${d.shelf.length} on the wall` : '');
      }
      if (d.liked?.length) setNote(`♥ saved to your likes`);
    } catch (err) {
      setMessages((cur) => [...cur, { role: 'assistant', text: err.message || 'The counter is busy — try again.' }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={wrap}>
      <div style={head}>
        <div>
          <div style={{ font: '600 14px system-ui', color: '#eef7ea' }}>{shop.name || 'The counter'}</div>
          <div style={{ font: '10.5px system-ui', letterSpacing: 1.1, textTransform: 'uppercase', color: ACCENT, opacity: 0.8 }}>
            {shop.role || 'record shop'}
          </div>
        </div>
        <button onClick={closeShop} style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#cfe8c6', fontSize: 20, lineHeight: 1, padding: 4 }} aria-label="Leave the counter">×</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 13px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!messages.length && (
          <>
            <div style={{ ...bubble(false) }}>
              What are you after? Give me a genre, a tempo, or just a feeling — I&apos;ll pull something.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
              {OPENERS.map((o) => (
                <button key={o} style={chip} onClick={() => send(o)}>{o}</button>
              ))}
            </div>
          </>
        )}
        {messages.map((m, i) => <div key={i} style={bubble(m.role === 'user')}>{m.text}</div>)}
        {busy && <div style={{ ...bubble(false), opacity: 0.6 }}>flipping through the racks…</div>}
        {note && <div style={{ alignSelf: 'flex-start', font: '11.5px system-ui', color: ACCENT, opacity: 0.85 }}>{note}</div>}
        <div ref={endRef} />
      </div>

      {IS_GUEST && (
        <div style={{ padding: '0 13px 6px', font: '11px system-ui', color: 'rgba(255,255,255,0.5)' }}>
          Browsing as a guest — sign in to save records to your likes.
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        style={{ display: 'flex', gap: 7, padding: '10px 12px 12px', borderTop: '1px solid rgba(157,255,125,0.16)' }}
      >
        <input
          style={inputStyle}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Looking for something…"
          disabled={busy}
          autoFocus
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          style={{
            cursor: busy || !input.trim() ? 'default' : 'pointer', border: 'none', borderRadius: 10,
            padding: '0 14px', font: '700 14px system-ui', color: '#08120a',
            background: busy || !input.trim() ? 'rgba(157,255,125,0.28)' : ACCENT,
          }}
        >Ask</button>
      </form>
    </div>
  );
}
